import React, { useEffect, useState } from 'react';
import PageContainer from '../../../components/PageContainer';
import { Box, Button, ButtonGroup, CircularProgress, Dialog, DialogActions, DialogContent, Divider, Grid, IconButton, InputAdornment } from '@mui/material';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { AccountBalance, Add, ArrowBack, DeleteOutline, Groups, GroupsOutlined, Home, People, Refresh, Search } from '@mui/icons-material';
import TextInput from '../../../components/common/TextInput';
import SelectInput from '../../../components/common/SelectInput';
import { Link, useHistory, useParams } from 'react-router-dom';
import appRoutes from '../../../routes/routeConfig';
import { assignUserRole, createUser, editUser, getADDetailsByUsername, getRoles, getUserById, getUserRoles, removeUserRole } from '../../../redux/api/userMgt';
import { useSnackbar } from 'notistack';
import { DataGrid } from '@mui/x-data-grid';

const UserCreate = () => {
    const history = useHistory();
    const params = useParams();
    const { enqueueSnackbar } = useSnackbar();
    const formFields = {
        username: '',
        password: '',
        confirmPassword: '',
        lastName: '',
        middleName: '',
        firstName: '',
        employeeId: '',
        email: '',
    };
    const defRole = { roleName: '', userId: '' };
    const [user, setUser] = useState(formFields);
    const [processing, setProcessing] = useState(false);
    const [validated, setValidated] = useState(false);
    const [validating, setValidating] = useState(false);
    const [selectedRole, setSelectedRole] = useState(defRole);
    const [isEditing, setIsEditing] = useState(false);
    const [allRoles, setAllRoles] = useState([]);
    const [actionModal, setActionModal] = useState(null);
    const [section, setSection] = useState(0);
    const sections = ['User Details', 'User Roles'];

    const roleColumns = [
        {
            field: 'sn',
            headerName: 'S/N',
            width: 100,
            sortable: false,
            renderCell: (params) => {
                return <p style={{ margin: 'auto' }}>{(params?.api?.getRowIndex(params?.row?.roleName) || 0) + 1}</p>;
            },
        },
        {
            field: 'action',
            headerName: 'Quick Action',
            width: 150,
            sortable: false,
            renderCell: (params) => {
                return (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                        <IconButton
                            size="small"
                            color={'error'}
                            onClick={() => {
                                setSelectedRole(params?.row || null);
                                setActionModal('remove');
                            }}
                            variant="outlined"
                            title={'Remove role'}
                        >
                            <DeleteOutline />
                        </IconButton>
                    </div>
                );
            },
        },
        { field: 'roleName', headerName: 'Role', width: 300 },
        { field: 'roleDesc', headerName: 'Description', width: 350 },
    ];

    const getUserInfo = async (userId) => {
        try {
            setProcessing(true);
            const response = await getUserById(userId);
            const resp = await getUserRoles(userId);
            setUser({
                ...response,
                userRoles: resp,
            });
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', { variant: 'error' });
        } finally {
            setProcessing(false);
        }
    };

    const searchDirectory = async (username) => {
        setValidating(true);
        try {
            const resp = await getADDetailsByUsername(username);
            const details = resp?.resultObject || null;
            if (details) {
                setUser({
                    ...details,
                    email: details.mail,
                });
                setValidated(true);
            } else {
                setValidated(false);
            }
        } catch (err) {
            console.log(err);
            enqueueSnackbar(err?.message || 'An error occurred', { variant: 'error' });
        } finally {
            setValidating(false);
        }
    };

    const init = async (userId) => {
        try {
            const roles = await getRoles();
            setAllRoles(roles);

            if (userId) {
                const resp = await getUserRoles(userId);
                setUser({
                    ...user,
                    userRoles: resp,
                });
            }
        } catch (err) {
            console.log(err);
        }
    };

    const submitForm = async (values, { resetForm }) => {
        try {
            let resp;
            if (isEditing) {
                resp = await editUser(values);
            } else {
                resp = await createUser(values);
            }
            if (resp?.isSuccess) {
                enqueueSnackbar(isEditing ? 'User Updated Successfully' : 'User Created Successfully', { variant: 'success' });
                history.push(appRoutes.internal.users.root);
                resetForm();
            } else {
                enqueueSnackbar(resp?.message || 'An error occurred', { variant: 'error' });
            }
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', { variant: 'error' });
        }
    };

    const remove = async (values) => {
        try {
            await removeUserRole({ ...values, roleName: selectedRole?.roleName });
            setActionModal(false);
            enqueueSnackbar('Role Removed Successfully');
            init(values?.userId);
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', { variant: 'error' });
        }
    };

    const add = async (values, { resetForm }) => {
        try {
            await assignUserRole(values);
            setActionModal(false);
            enqueueSnackbar('Role Added Successfully');
            resetForm();
            init(values?.userId);
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', { variant: 'error' });
        }
    };

    useEffect(() => {
        if (params?.userId) {
            setIsEditing(true);
            getUserInfo(params?.userId);
            init();
        } else {
            setUser(formFields);
            setIsEditing(false);
        }
    }, [params]);

    return (
        <PageContainer
            pageTitle="Create CBN User"
            tagline="Create cbn User"
            processing={processing}
            buttonComponent={
                <>
                    <Link to={appRoutes.internal.users.root}>
                        <Button startIcon={<ArrowBack />} variant="outlined">
                            Go Back
                        </Button>
                    </Link>
                </>
            }
        >
            {isEditing && (
                <ButtonGroup variant="outlined" className="pl-2 pr-2 mb-2" fullWidth>
                    {sections.map((sec, key) => (
                        <Button variant={section === key ? 'contained' : 'outlined'} key={key} onClick={() => setSection(key)}>
                            {sec}
                        </Button>
                    ))}
                </ButtonGroup>
            )}

            {section == 0 && (
                <div className="paper">
                    <Formik
                        initialValues={{ ...user }}
                        enableReinitialize
                        onSubmit={submitForm}
                        validationSchema={Yup.object().shape({
                            lastName: Yup.string().required('Last Name is required.'),
                            firstName: Yup.string().required('First Name is required.'),
                            email: Yup.string().email('Value must be a valid email address').required('Email is required.'),
                            employeeId: Yup.string().required('Employee Id is required.'),
                        })}
                    >
                        {({ isSubmitting }) => (
                            <Form>
                                <Grid container spacing={3} paddingBottom={3}>
                                    <Grid item xs={12} md={6} lg={4}>
                                        <TextInput id="username" required label="Username (Surname + ID Number)" placeholder="Enter Username" className="no-direct-child-padding" />
                                    </Grid>

                                    <Grid item xs={12} md={6} lg={4}>
                                        <TextInput id="firstName" required label="First Name" />
                                    </Grid>

                                    <Grid item xs={12} md={6} lg={4}>
                                        <TextInput id="lastName" required label="Last name" />
                                    </Grid>
                                    <Grid item xs={12} md={6} lg={4}>
                                        <TextInput id="email" required label="Email" type="email" />
                                    </Grid>
                                    <Grid item xs={12} md={6} lg={4}>
                                        <TextInput id="employeeId" required label="Employee ID" />
                                    </Grid>
                                    <Grid item xs={12} md={6} lg={4}>
                                        <TextInput id="phoneNumber" label="Phone Number" />
                                    </Grid>
                                    <Grid item xs={12} md={6} lg={4} container justifyContent={'flex-end'} flexDirection="column">
                                        <Button disabled={isSubmitting} startIcon={!isEditing && <Add />} fullWidth variant="contained" type="submit" style={{ marginTop: '.5rem' }}>
                                            {isSubmitting ? <CircularProgress size={15} color="inherit" /> : isEditing ? 'Edit User' : 'Add User'}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Form>
                        )}
                    </Formik>
                </div>
            )}

            {section == 1 && (
                <>
                    <Grid container justifyContent={'flex-end'}>
                        <Button
                            variant="contained"
                            className="mr-2"
                            color="info"
                            onClick={() => {
                                setActionModal('add');
                            }}
                        >
                            Add Role
                        </Button>
                    </Grid>
                    <Box>
                        <div className="paper" style={{ height: 500, width: '100%', textAlign: 'center' }}>
                            <DataGrid
                                className={'center-first-column'}
                                rows={user?.userRoles ?? []}
                                getRowId={(row) => row.roleName}
                                columns={roleColumns}
                                pageSize={10}
                                rowsPerPageOptions={[10]}
                                checkboxSelection={false}
                                disableSelectionOnClick
                            />
                        </div>
                    </Box>

                    <Dialog open={Boolean(actionModal)} maxWidth="xs" fullWidth>
                        <Formik initialValues={{ userId: user?.id }} enableReinitialize onSubmit={actionModal === 'add' ? add : remove}>
                            {({ isSubmitting }) => (
                                <Form>
                                    <DialogContent>
                                        {actionModal === 'add' ? (
                                            <>
                                                <h4 className="mb-1">Add Role</h4>
                                                <SelectInput
                                                    id="roleName"
                                                    label="Role Name"
                                                    required
                                                    fieldDisplay="roleName"
                                                    field="roleName"
                                                    options={allRoles.filter(({ roleName }) => !String(roleName).startsWith('FI'))}
                                                />
                                            </>
                                        ) : (
                                            <>
                                                <h4 className="mb-1">Remove Role</h4>
                                                <p>
                                                    Kindly confirm deletion of role with role name: {selectedRole?.roleName} for user: {user?.lastName} {user?.firstName}
                                                </p>
                                            </>
                                        )}
                                    </DialogContent>
                                    <DialogActions className="pb-1 pr-1">
                                        <Button variant="contained" color="secondary" onClick={() => setActionModal(null)} disabled={isSubmitting}>
                                            Cancel
                                        </Button>
                                        <Button variant="contained" color={actionModal === 'add' ? 'primary' : 'error'} type="submit" disabled={isSubmitting}>
                                            {Boolean(isSubmitting) ? <CircularProgress color="inherit" size={15} /> : actionModal === 'add' ? 'Add' : 'Remove'}
                                        </Button>
                                    </DialogActions>
                                </Form>
                            )}
                        </Formik>
                    </Dialog>
                </>
            )}
        </PageContainer>
    );
};

export default UserCreate;
